import React, { useEffect, useState } from 'react';
import { useSyncStore } from '../lib/offline/sync-store';
import { performSync } from '../lib/offline/sync-engine';
import { refreshSyncCounts } from '../lib/offline/sync-counts';
import { Loader2, WifiOff, AlertTriangle, CloudOff, RefreshCw, Clock, XCircle } from 'lucide-react';
import ConflictReviewPanel from './ConflictReviewPanel';
import FailedSyncPanel from './FailedSyncPanel';
import { Tooltip } from './ui/tooltip';

function relativeTime(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/** The problem bar's age of the last good sync: never "just now" beside a failure. */
function sinceLastGoodSync(isoString: string): string {
  const rel = relativeTime(isoString);
  return rel === 'just now' ? 'under a minute ago' : rel;
}

/**
 * "3 changes + 1 photo", or null when nothing is queued.
 *
 * Pulled out of the pending bar because two states now need the same sentence:
 * that bar, and the offline notices above it. The offline bar used to say only
 * "Offline — changes will sync when connection returns" and OUTRANKED the
 * pending count, so the one moment a hand most needs to know how much work is
 * sitting on the device unsent was the one moment the app would not tell them.
 */
function queuedWork(mutations: number, uploads: number): string | null {
  const parts: string[] = [];
  if (mutations > 0) parts.push(`${mutations} change${mutations > 1 ? 's' : ''}`);
  if (uploads > 0) parts.push(`${uploads} photo${uploads > 1 ? 's' : ''}`);
  return parts.length > 0 ? parts.join(' + ') : null;
}

const SyncStatusBar: React.FC = () => {
  const {
    isOnline, isSyncing, pendingMutations, pendingUploads,
    failedMutations, failedUploads,
    conflicts, conflictDetails, lastSyncAt, setLastSyncAt, setSyncing,
    setConflicts, setError, loadConflictDetails,
    cacheDegraded, sessionFromCache, serverUnreachable, error,
  } = useSyncStore();
  const [showConflicts, setShowConflicts] = useState(false);
  const [showFailed, setShowFailed] = useState(false);
  const [manualSyncing, setManualSyncing] = useState(false);

  // Seed the counters on mount. Without this the failed bar would only appear
  // after the first sync cycle of the session — i.e. work that failed
  // yesterday would stay invisible until the app happened to try again.
  useEffect(() => {
    void refreshSyncCounts();
    void loadConflictDetails();
  }, [loadConflictDetails]);

  const handleForceSync = async () => {
    if (isSyncing || manualSyncing || !isOnline) return;
    setManualSyncing(true);
    setSyncing(true);
    try {
      const result = await performSync();
      // Client clock, and only for the "Synced 3m ago" label — the server
      // cursor is written inside performSync from server time.
      setLastSyncAt(result.timestamp);
      setConflicts(result.pushed.conflicts);
      setError(null);
      useSyncStore.getState().setServerUnreachable(false);
    } catch (err: any) {
      setError(err.message || 'Sync failed');
    } finally {
      setSyncing(false);
      setManualSyncing(false);
      // In `finally`, not on the success path. `performSync` now rejects when
      // the pull fails, and the push before it may still have produced applied
      // rows, conflicts and failures the user needs to see.
      await refreshSyncCounts();
      await loadConflictDetails();
    }
  };

  // Conflicts, and applied-with-a-notice entries (api#1352) in the same log.
  // A notice is a change that SAVED, so it is not counted as a conflict. Only
  // when every entry is a notice does the bar say so, in amber.
  if (conflicts > 0) {
    const notices = conflictDetails.filter(c => c.kind === 'warning').length;
    const realConflicts = conflictDetails.length > 0 ? conflictDetails.length - notices : conflicts;
    const onlyNotices = realConflicts === 0 && notices > 0;
    return (
      <>
        <button
          onClick={() => setShowConflicts(true)}
          className={`w-full text-white text-xs font-medium px-4 py-1.5 flex items-center gap-2 justify-center transition-colors ${
            onlyNotices ? 'bg-amber-700/90 hover:bg-amber-700' : 'bg-red-600/90 hover:bg-red-600'
          }`}
        >
          <AlertTriangle size={14} />
          <span>
            {onlyNotices
              ? `${notices} sync notice${notices > 1 ? 's' : ''} — tap to review`
              : `${realConflicts} conflict${realConflicts > 1 ? 's' : ''} — tap to review`}
          </span>
        </button>
        {showConflicts && <ConflictReviewPanel onClose={() => setShowConflicts(false)} />}
      </>
    );
  }

  // Work that has stopped retrying.
  //
  // Ranked above every other state below — including offline and syncing —
  // because it is the only one that is not going to resolve itself. "Offline"
  // and "3 changes waiting" both end when coverage returns; this does not. It
  // is also the state that used to render NOTHING AT ALL: at retryCount 3 the
  // mutation left the flush and the pending count together, the orange bar
  // vanished, and a vanished bar is this app's only "you are caught up"
  // signal. The bar has to outrank the states that would hide it.
  const failedTotal = failedMutations + failedUploads;
  if (failedTotal > 0) {
    return (
      <>
        <button
          onClick={() => setShowFailed(true)}
          className="w-full bg-red-700 text-white text-xs font-medium px-4 py-1.5 flex items-center gap-2 justify-center hover:bg-red-600 transition-colors"
        >
          <XCircle size={14} />
          <span>
            {failedTotal} item{failedTotal > 1 ? 's' : ''} did not save — tap to review
          </span>
        </button>
        {showFailed && <FailedSyncPanel onClose={() => setShowFailed(false)} />}
      </>
    );
  }

  // Offline cache sealed — outranks the plain offline notice below, because
  // "changes will sync when connection returns" is not true in this state.
  // The user is online-only for this session; if they drive out of coverage
  // they have nothing cached, so they need to know before that happens rather
  // than discover it in a dead zone.
  if (cacheDegraded) {
    return (
      <div className="bg-amber-700/90 text-white text-xs font-medium px-4 py-1.5 flex items-center gap-2 justify-center">
        <AlertTriangle size={14} />
        <span>Offline storage unavailable — you must stay connected. Restart the app to retry.</span>
      </div>
    );
  }

  const queued = queuedWork(pendingMutations, pendingUploads);

  // Opened from the profile saved on this device, because /auth/me could not be
  // reached. Ranked above the plain offline notice because it says something
  // that notice does not: nothing on screen has been confirmed by the server
  // this session, and the sign-in itself has not been re-checked.
  //
  // It renders whatever `isOnline` says. The two are not the same claim — the
  // detector can still read online (radio up, one bar, an API that is down)
  // while we know for a fact the API did not answer at boot, and this bar is
  // the app's only report of that.
  if (sessionFromCache) {
    return (
      <div className="bg-yellow-600/90 text-white text-xs font-medium px-4 py-1.5 flex items-center gap-2 justify-center">
        <WifiOff size={14} />
        <span>
          {queued
            ? `Working offline from a saved sign-in — ${queued} waiting to sync`
            : 'Working offline from a saved sign-in — changes will sync when connection returns'}
        </span>
      </div>
    );
  }

  // Offline
  if (!isOnline) {
    const staleWarning = lastSyncAt && (Date.now() - new Date(lastSyncAt).getTime() > 60 * 60 * 1000);
    return (
      <div className="bg-yellow-600/90 text-white text-xs font-medium px-4 py-1.5 flex items-center gap-2 justify-center">
        <WifiOff size={14} />
        <span>
          {queued
            ? `Offline — ${queued} waiting to sync`
            : staleWarning
              ? `Offline — data may be outdated (synced ${relativeTime(lastSyncAt!)})`
              : 'Offline — changes will sync when connection returns'}
        </span>
      </div>
    );
  }

  // Syncing
  if (isSyncing) {
    return (
      <div className="bg-blue-600/90 text-white text-xs font-medium px-4 py-1.5 flex items-center gap-2 justify-center">
        <Loader2 size={14} className="animate-spin" />
        <span>Syncing...</span>
      </div>
    );
  }

  // Pending mutations/uploads
  if (queued) {
    return (
      <div className="bg-orange-600/90 text-white text-xs font-medium px-4 py-1.5 flex items-center gap-2 justify-center">
        <CloudOff size={14} />
        <span>{queued} waiting to sync</span>
        <Tooltip content="Sync now">
          <button onClick={handleForceSync} className="ml-1 hover:text-amber-200" aria-label="Sync now">
            <RefreshCw size={13} />
          </button>
        </Tooltip>
      </div>
    );
  }

  // The last request to the API got no answer, or the last sync failed.
  //
  // RANKED ABOVE "Synced …" AND THAT IS THE WHOLE FIX. The detector needs two
  // failed probes before it shows the offline bar, and the five-minute sync
  // swallowed its own failure, so with the API down this bar went on reading
  // "Synced just now" — a claim to be in touch with the server, made over a
  // request that had just failed. The time of the last good sync is still
  // shown, because it is still true; "just now" is not.
  if (serverUnreachable || error) {
    return (
      <div
        data-testid="sync-bar-problem"
        className="bg-fieldiq-card text-fieldiq-text text-[10px] px-4 py-1 flex items-center gap-1.5 justify-center"
      >
        <AlertTriangle size={10} aria-hidden="true" />
        <span>
          {serverUnreachable ? 'Can’t reach the server' : 'Last sync didn’t finish'}
          {lastSyncAt ? ` — last synced ${sinceLastGoodSync(lastSyncAt)}` : ' — nothing has synced yet this session'}
        </span>
        <Tooltip content="Try again">
          <button onClick={handleForceSync} className="ml-1 hover:text-fieldiq-accent" aria-label="Try to sync again">
            <RefreshCw size={10} />
          </button>
        </Tooltip>
      </div>
    );
  }

  // All good — show last synced time subtly
  if (lastSyncAt) {
    return (
      <div className="bg-fieldiq-card/50 text-fieldiq-muted text-[10px] px-4 py-1 flex items-center gap-1.5 justify-center">
        <Clock size={10} />
        <span>Synced {relativeTime(lastSyncAt)}</span>
        <Tooltip content="Sync now">
          <button onClick={handleForceSync} className="ml-1 hover:text-fieldiq-accent" aria-label="Sync now">
            <RefreshCw size={10} />
          </button>
        </Tooltip>
      </div>
    );
  }

  return null;
};

export default SyncStatusBar;
